import React from "react";

/**
 * Branded SIM card — inline SVG, resolution-independent, uses PTT NZ colors.
 * Two variants:
 *   variant="sim"   → physical SIM (default)
 *   variant="esim"  → adds a small eSIM chip glyph & "eSIM" wordmark
 *
 * Sizes: pass className to control (aspect ratio locked by viewBox 640×400).
 */
const BrandedSim = ({ variant = "sim", className = "" }) => {
  const isEsim = variant === "esim";
  const isSimple = variant === "simple" || variant === "esim-simple";
  const isSimpleEsim = variant === "esim-simple";

  // Compact simplified variant for small thumbnails
  if (isSimple) {
    return (
      <svg
        viewBox="0 0 640 400"
        role="img"
        aria-label={isSimpleEsim ? "PTT NZ Multi-Network eSIM" : "PTT NZ Multi-Network SIM"}
        className={className}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="s-body" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#0F1717" />
            <stop offset="1" stopColor="#0A0A0A" />
          </linearGradient>
          <linearGradient id="s-chip" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#F4D68A" />
            <stop offset="0.5" stopColor="#E1B657" />
            <stop offset="1" stopColor="#B58732" />
          </linearGradient>
          <radialGradient id="s-glow" cx="0.85" cy="0.15" r="0.7">
            <stop offset="0" stopColor="#2EC4A5" stopOpacity="0.5" />
            <stop offset="1" stopColor="#2EC4A5" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect x="10" y="10" width="620" height="380" rx="28" fill="url(#s-body)" />
        <rect x="10" y="10" width="620" height="380" rx="28" fill="url(#s-glow)" />
        <rect x="10" y="10" width="620" height="380" rx="28" fill="none" stroke="#1A1F1F" />

        {/* Chip */}
        <g transform="translate(70 130)">
          <rect x="0" y="0" width="180" height="140" rx="16" fill="url(#s-chip)" />
          <g stroke="#8A6520" strokeOpacity="0.55" strokeWidth="2" fill="none">
            <line x1="0"   y1="45"  x2="60"  y2="45" />
            <line x1="0"   y1="95"  x2="60"  y2="95" />
            <line x1="120" y1="45"  x2="180" y2="45" />
            <line x1="120" y1="95"  x2="180" y2="95" />
            <line x1="60"  y1="0"   x2="60"  y2="140" />
            <line x1="120" y1="0"   x2="120" y2="140" />
            <line x1="60"  y1="70"  x2="120" y2="70" />
          </g>
          <rect x="72" y="52" width="36" height="36" fill="#0A0A0A" />
          <rect x="78" y="58" width="24" height="24" fill="#2EC4A5" />
        </g>

        {/* Signal-wave icon */}
        <g transform="translate(504 60)" fill="none" stroke="#2EC4A5" strokeWidth="7" strokeLinecap="round">
          <path d="M0 56 A 56 56 0 0 1 56 0" />
          <path d="M16 56 A 40 40 0 0 1 56 16" strokeOpacity="0.85" />
          <path d="M32 56 A 24 24 0 0 1 56 32" strokeOpacity="0.7" />
          <circle cx="56" cy="56" r="6" fill="#2EC4A5" stroke="none" />
        </g>

        {/* Wordmark */}
        <g transform="translate(288 178)" fill="#F5F5F5" fontFamily="'Bricolage Grotesque','Manrope',sans-serif">
          <text x="0" y="0" fontSize="60" fontWeight="700" letterSpacing="-1.5">PTT NZ</text>
          <text x="0" y="34" fontSize="16" fontWeight="500" letterSpacing="4" fill="#2EC4A5">
            {isSimpleEsim ? "MULTI-NETWORK eSIM" : "MULTI-NETWORK SIM"}
          </text>
        </g>

        {isSimpleEsim && (
          <g transform="translate(504 300)">
            <rect x="0" y="0" width="80" height="30" rx="4" fill="#2EC4A5" />
            <text x="40" y="21" textAnchor="middle" fontFamily="'Bricolage Grotesque','Manrope',sans-serif" fontSize="15" fontWeight="700" fill="#0A0A0A">eSIM</text>
          </g>
        )}
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 640 400"
      role="img"
      aria-label={isEsim ? "PTT NZ Global Multi-Network eSIM" : "PTT NZ Global Multi-Network SIM"}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Card body gradient — deep ink with subtle teal glow */}
        <linearGradient id="cardBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0F1717" />
          <stop offset="1" stopColor="#0A0A0A" />
        </linearGradient>
        {/* Chip gold gradient */}
        <linearGradient id="chipMetal" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F4D68A" />
          <stop offset="0.5" stopColor="#E1B657" />
          <stop offset="1" stopColor="#B58732" />
        </linearGradient>
        {/* Teal signal glow */}
        <radialGradient id="signalGlow" cx="0.85" cy="0.15" r="0.6">
          <stop offset="0" stopColor="#2EC4A5" stopOpacity="0.55" />
          <stop offset="1" stopColor="#2EC4A5" stopOpacity="0" />
        </radialGradient>
        {/* Card shine */}
        <linearGradient id="shine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        {/* Fine grid pattern */}
        <pattern id="grid" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Card body */}
      <rect x="10" y="10" width="620" height="380" rx="24" fill="url(#cardBody)" />
      <rect x="10" y="10" width="620" height="380" rx="24" fill="url(#grid)" />
      <rect x="10" y="10" width="620" height="380" rx="24" fill="url(#signalGlow)" />
      <rect x="10" y="10" width="620" height="380" rx="24" fill="url(#shine)" />

      {/* Card outer bevel */}
      <rect x="10" y="10" width="620" height="380" rx="24" fill="none" stroke="#1A1F1F" strokeWidth="1" />

      {/* SIM chip */}
      <g transform="translate(48 96)">
        <rect x="0" y="0" width="200" height="160" rx="16" fill="url(#chipMetal)" />
        <rect x="0" y="0" width="200" height="160" rx="16" fill="none" stroke="#8A6520" strokeOpacity="0.35" strokeWidth="1" />
        {/* Chip contact lines */}
        <g stroke="#8A6520" strokeOpacity="0.55" strokeWidth="2" fill="none">
          <line x1="0"   y1="52"  x2="70"  y2="52" />
          <line x1="0"   y1="108" x2="70"  y2="108" />
          <line x1="130" y1="52"  x2="200" y2="52" />
          <line x1="130" y1="108" x2="200" y2="108" />
          <line x1="70"  y1="0"   x2="70"  y2="160" />
          <line x1="130" y1="0"   x2="130" y2="160" />
          <line x1="70"  y1="80"  x2="130" y2="80" />
        </g>
        {/* Centre PTT teal square */}
        <rect x="82" y="62" width="36" height="36" fill="#0A0A0A" />
        <rect x="88" y="68" width="24" height="24" fill="#2EC4A5" />
      </g>

      {/* PTT NZ signal-wave icon (top-right) */}
      <g transform="translate(538 46)" fill="none" stroke="#2EC4A5" strokeWidth="6" strokeLinecap="round">
        {/* radiating arcs */}
        <path d="M0 44 A 44 44 0 0 1 44 0" />
        <path d="M12 44 A 32 32 0 0 1 44 12" strokeOpacity="0.85" />
        <path d="M24 44 A 20 20 0 0 1 44 24" strokeOpacity="0.7" />
        {/* base dot */}
        <circle cx="44" cy="44" r="5" fill="#2EC4A5" stroke="none" />
      </g>

      {/* Brand wordmark */}
      <g transform="translate(288 118)" fill="#F5F5F5" fontFamily="'Bricolage Grotesque','Manrope',sans-serif">
        <text x="0" y="0" fontSize="46" fontWeight="700" letterSpacing="-1">PTT NZ</text>
        <text x="0" y="38" fontSize="14" fontWeight="500" letterSpacing="3" fill="#2EC4A5">
          {isEsim ? "GLOBAL · MULTI-NETWORK eSIM" : "GLOBAL · MULTI-NETWORK SIM"}
        </text>
      </g>

      {/* Mid-card divider */}
      <line x1="288" y1="176" x2="592" y2="176" stroke="#2A2F2F" strokeWidth="1" />

      {/* Feature strip */}
      <g transform="translate(288 200)" fill="#B8BCBC" fontFamily="'JetBrains Mono',monospace" fontSize="11" letterSpacing="1.5">
        <text x="0" y="0">3 NZ CARRIERS · STRONGEST SIGNAL WINS</text>
        <text x="0" y="22">AU · TELSTRA · VODAFONE · OPTUS</text>
        <text x="0" y="44">190+ COUNTRIES · 600+ CARRIERS</text>
      </g>

      {/* Bottom carrier strip */}
      <g transform="translate(48 314)">
        <rect x="0" y="0" width="544" height="42" rx="6" fill="#151B1B" stroke="#232929" />
        <g fontFamily="'Bricolage Grotesque','Manrope',sans-serif" fontWeight="700" fontSize="13" fill="#F5F5F5">
          <text x="26"  y="27">SPARK</text>
          <text x="120" y="27">2DEGREES</text>
          <text x="240" y="27">ONE NZ</text>
          <text x="340" y="27" fill="#2EC4A5">·</text>
          <text x="360" y="27">AU</text>
          <text x="410" y="27" fill="#2EC4A5">·</text>
          <text x="430" y="27">GLOBAL</text>
        </g>
      </g>

      {/* ICCID label */}
      <g transform="translate(48 268)" fill="#6B7271" fontFamily="'JetBrains Mono',monospace" fontSize="10" letterSpacing="2">
        <text x="0" y="0">ICCID · 8964 0212 0000 0000 000</text>
      </g>

      {/* eSIM badge (variant only) */}
      {isEsim && (
        <g transform="translate(524 118)">
          <rect x="0" y="0" width="70" height="26" rx="4" fill="#2EC4A5" />
          <text x="35" y="18" textAnchor="middle" fontFamily="'Bricolage Grotesque','Manrope',sans-serif" fontSize="13" fontWeight="700" fill="#0A0A0A">eSIM</text>
        </g>
      )}
    </svg>
  );
};

export default BrandedSim;
